/**
 * SCM11 — RFQ template generator.
 *
 * Given a BoM item (or batch of items) + project context + a vendor
 * response deadline, this module builds a fillable Request-For-Quote
 * DOCX that the user can email/upload to a supplier. The supplier
 * fills in the form fields, returns a PDF, and SCM8's
 * `extractQuoteFromPdf` parses it back into a `Quote` record — closing
 * the loop end-to-end.
 *
 * Round-trip discipline:
 *   The vendor-fill labels emitted here MUST match the patterns in
 *   `quotePatterns.ts` so that the produced DOCX (rendered to PDF and
 *   filled in by the supplier) parses cleanly back through
 *   `extractQuoteFromPdf`. The unit test pins this contract.
 *
 *   Currently matched labels:
 *     - "Unit Price: $______________"                → unitCostUsd
 *     - "Lead Time: _____ months ARO"                → leadTimeMonths
 *     - "Quote Validity: _____ days from issue"      → validityDays
 *     - "Payment Terms: _____________________"       → paymentTerms
 *     - "Incoterms: ______ (FOB / DDP / CIF / EXW)"  → freightTerms
 *     - "Currency: _____ (USD / EUR / etc.)"         → currency
 *
 * RFQ id format: `rfq-{siteId}-{bomItemId}-{seq}`. The caller passes
 * the seq number (defaulting to 1) so retransmits stay stable.
 *
 * Pure-function: this module returns a `docx` `Document` and a
 * `Blob` — it does NOT touch the filesystem, fetch anything, or
 * trigger a download. The UI layer owns the `saveAs` call so that
 * tests can exercise this without DOM globals.
 */
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  HeadingLevel,
  Table,
  TableRow,
  TableCell,
  WidthType,
  AlignmentType,
  BorderStyle,
  Footer,
  Header,
  ShadingType,
} from 'docx';
import type { BomItem, SCMCategory } from '../../types/supplyChain';
import { SCM_CATEGORY_LABELS } from '../../types/supplyChain';
import {
  BOM_CRITICALITY_FIELD_LABEL,
  BOM_SUBSTITUTABILITY_FIELD_LABEL,
  labelBomCriticality,
  labelBomSubstitutability,
} from '../../lib/bomFieldLabels';

// ─────────────────────────────────────────────────────────────────────────────
// Input shapes
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Per-RFQ project context. Stamped into the document so the vendor
 * has the destination + COD without having to chase emails. None of
 * these fields round-trip through `extractQuoteFromPdf` — they're
 * static metadata for the vendor's benefit.
 */
export interface RfqProjectContext {
  siteId: string;
  /** Display name of the site / project, e.g. "Hyperscaler AI DC — Phoenix". */
  siteName: string;
  /** Mailing/shipping address as a single free-text line. */
  siteAddress: string;
  /** ISO date — commercial operations / in-service date. */
  cod?: string | null;
  /** Selected technology label (e.g. "Small Modular Reactor (SMR)"). */
  technologyLabel?: string | null;
  /** Issuing company / EPC / owner — appears in the "from" line. */
  issuingCompany?: string;
  /** Contact name + email for the vendor to reply to. */
  contactName?: string;
  contactEmail?: string;
}

/**
 * Options for a single RFQ. Required: bomItem + project + the deadline
 * for vendor response. Optional: seq (for retransmits / multiple RFQs
 * to different vendors on the same item), supplierName (so the doc
 * addresses the recipient).
 */
export interface RfqOptions {
  bomItem: BomItem;
  project: RfqProjectContext;
  /** ISO timestamp — issue date stamped on the document. Defaults to now(). */
  issueDate?: string;
  /** ISO timestamp — vendor must respond by this date. */
  responseDeadline: string;
  /** Sequence number — appended to the RFQ id. Default 1. */
  seq?: number;
  /** Recipient — surfaces on the "TO:" line. */
  supplierName?: string;
}

export interface BuiltRfq {
  rfqId: string;
  /** Suggested filename — sanitised to the same chars as reportDocxExport. */
  filename: string;
  /** Plain-text rendering of the form. Surfaced for tests + round-trip
   *  verification through `extractQuoteFromPdf`. Mirrors the visible
   *  text in the DOCX (modulo table layout). */
  plainText: string;
  /** The docx `Document` — pass to `Packer.toBlob(doc)` to render. */
  document: Document;
}

// ─────────────────────────────────────────────────────────────────────────────
// RFQ id + filename helpers
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Stable RFQ id. Same (siteId, bomItemId, seq) always produces the
 * same string so retransmits don't proliferate ids in audit logs.
 */
export function buildRfqId(siteId: string, bomItemId: string, seq = 1): string {
  return `rfq-${siteId}-${bomItemId}-${seq}`;
}

/**
 * Filesystem-safe filename. Mirrors the `safe()` helper inside
 * `reportDocxExport.ts` — strips path-meta chars and clamps length.
 */
export function buildRfqFilename(rfqId: string, siteName: string, itemName: string): string {
  const safe = (s: string): string =>
    s.replace(/[\/\\:*?"<>|\r\n]/g, '_').replace(/\s+/g, ' ').trim().slice(0, 80);
  return `Datacrate — RFQ — ${safe(siteName)} — ${safe(itemName)} — ${safe(rfqId)}.docx`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Date helpers
// ─────────────────────────────────────────────────────────────────────────────

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  // YYYY-MM-DD is what the docs already use in supplyChain.ts; keep
  // tabular rendering stable.
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toISOString().slice(0, 10);
}

// ─────────────────────────────────────────────────────────────────────────────
// Plain-text builder (round-trip target)
//
// This produces exactly what the vendor will read in the DOCX. We
// emit it separately from the rich Document so the test suite can
// pipe it through extractQuoteFromPdf and pin the round-trip.
// ─────────────────────────────────────────────────────────────────────────────

const HR = '─'.repeat(72);

/**
 * Build the plain-text body. Labels are intentionally aligned with
 * the regex anchors in `quotePatterns.ts` — see the module header
 * for the table of matched fields.
 *
 * NB: the underscore strings are vendor-fill placeholders. When a
 * supplier fills these in (e.g. types "4,500,000" between the
 * underscores, or replaces them entirely) the resulting line still
 * matches the SCM8 patterns because those patterns are anchored on
 * the LABEL, not the underscore glyph.
 */
export function buildRfqPlainText(opts: RfqOptions): string {
  const item = opts.bomItem;
  const proj = opts.project;
  const issueDate = opts.issueDate ?? new Date().toISOString();
  const rfqId = buildRfqId(proj.siteId, item.id, opts.seq ?? 1);
  const compliance = (item.complianceRequirements ?? []).join(', ') || '—';

  const lines: string[] = [];
  lines.push(`REQUEST FOR QUOTE (RFQ)`);
  lines.push(``);
  lines.push(`RFQ ID:              ${rfqId}`);
  lines.push(`Issue Date:          ${fmtDate(issueDate)}`);
  lines.push(`Response Deadline:   ${fmtDate(opts.responseDeadline)}`);
  lines.push(`Project:             ${proj.siteName}`);
  if (proj.issuingCompany) lines.push(`Issued By:           ${proj.issuingCompany}`);
  if (opts.supplierName)   lines.push(`To:                  ${opts.supplierName}`);
  if (proj.contactName || proj.contactEmail) {
    lines.push(
      `Reply To:            ${proj.contactName ?? ''}${
        proj.contactEmail ? ` <${proj.contactEmail}>` : ''
      }`.trim(),
    );
  }
  lines.push(``);
  lines.push(HR);
  lines.push(`PROJECT CONTEXT`);
  lines.push(HR);
  lines.push(`Site Address:        ${proj.siteAddress}`);
  lines.push(`Commercial Op Date:  ${fmtDate(proj.cod)}`);
  lines.push(`Technology:          ${proj.technologyLabel ?? '—'}`);
  lines.push(
    `Required On-Site:    ${fmtDate(item.requiredOnSiteDate)}` +
      (item.requiredOnSiteDate ? '' : '  (derived from COD − install duration)'),
  );
  lines.push(``);
  lines.push(HR);
  lines.push(`ITEM DESCRIPTION`);
  lines.push(HR);
  lines.push(`Category:            ${SCM_CATEGORY_LABELS[item.category]}`);
  lines.push(`Item:                ${item.name}`);
  lines.push(`Specification:       ${item.spec || '—'}`);
  lines.push(`Quantity:            ${item.quantity} ${item.unit}`);
  lines.push(`Criticality:         ${labelBomCriticality(item.criticality)}`);
  lines.push(`Substitutability:    ${labelBomSubstitutability(item.substitutability)}`);
  lines.push(`Compliance Required: ${compliance}`);
  if (item.notes) lines.push(`Notes:               ${item.notes}`);
  lines.push(``);
  lines.push(HR);
  lines.push(`VENDOR RESPONSE — please fill the fields below`);
  lines.push(HR);
  // Labels here MUST match the SCM8 quote-extraction patterns. See
  // quotePatterns.ts. Do not rename without updating both ends.
  lines.push(`Unit Price:          $______________________________`);
  lines.push(`Lead Time:           __________ months ARO`);
  lines.push(`Quote Validity:      __________ days from issue`);
  lines.push(`Payment Terms:       ____________________________________________`);
  lines.push(`Incoterms:           __________   (FOB / DDP / CIF / EXW / CFR / DAP)`);
  lines.push(`Currency:            __________   (USD / EUR / GBP / etc.)`);
  lines.push(`Compliance Certs Attached: ___ Yes  ___ No   (attach copies)`);
  lines.push(`Notes / Exclusions:  ____________________________________________`);
  lines.push(``);
  lines.push(HR);
  lines.push(`STANDARD TERMS`);
  lines.push(HR);
  lines.push(
    `1. Confidentiality. This RFQ and any information disclosed by the issuer`,
  );
  lines.push(
    `   is confidential and shall not be shared with third parties without`,
  );
  lines.push(`   prior written consent.`);
  lines.push(
    `2. Response Format. Vendor may respond by completing this template and`,
  );
  lines.push(
    `   returning it as a signed PDF, or by attaching the vendor's standard`,
  );
  lines.push(
    `   quote document. Pricing, lead time, validity, payment terms and`,
  );
  lines.push(`   Incoterms must all be present in the response.`);
  lines.push(
    `3. Non-Binding. This RFQ is a request for information; it does not`,
  );
  lines.push(`   constitute a purchase order or commitment to purchase.`);
  lines.push(
    `4. Compliance. Vendor confirms ability to meet the certification`,
  );
  lines.push(`   requirements listed above, or shall clearly note exceptions.`);
  lines.push(``);
  lines.push(`Authorised Signature: ___________________________   Date: __________`);
  lines.push(``);
  return lines.join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
// DOCX builder — uses the docx library exactly like reportDocxExport.
// ─────────────────────────────────────────────────────────────────────────────

const BRAND = '0071df';
const DARK = '1f1f1f';
const GRAY = '6b7280';
const LIGHT_GRAY = 'f3f4f6';

function p(text: string, opts: { bold?: boolean; color?: string; size?: number } = {}): Paragraph {
  return new Paragraph({
    children: [
      new TextRun({
        text,
        bold: opts.bold,
        color: opts.color ?? DARK,
        size: opts.size ?? 20,
      }),
    ],
    spacing: { after: 80 },
  });
}

function h1(text: string): Paragraph {
  return new Paragraph({
    heading: HeadingLevel.HEADING_1,
    children: [
      new TextRun({ text, bold: true, color: BRAND, size: 32 }),
    ],
    spacing: { after: 200 },
  });
}

function h2(text: string): Paragraph {
  return new Paragraph({
    heading: HeadingLevel.HEADING_2,
    children: [
      new TextRun({ text, bold: true, color: DARK, size: 24 }),
    ],
    spacing: { before: 200, after: 120 },
  });
}

function kvRow(label: string, value: string): TableRow {
  return new TableRow({
    children: [
      new TableCell({
        width: { size: 35, type: WidthType.PERCENTAGE },
        shading: { type: ShadingType.CLEAR, color: 'auto', fill: LIGHT_GRAY },
        children: [p(label, { bold: true, color: GRAY })],
      }),
      new TableCell({
        width: { size: 65, type: WidthType.PERCENTAGE },
        children: [p(value)],
      }),
    ],
  });
}

function kvTable(rows: { label: string; value: string }[]): Table {
  return new Table({
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows: rows.map((r) => kvRow(r.label, r.value)),
    borders: {
      top:    { style: BorderStyle.SINGLE, size: 4, color: 'd0d5dd' },
      bottom: { style: BorderStyle.SINGLE, size: 4, color: 'd0d5dd' },
      left:   { style: BorderStyle.SINGLE, size: 4, color: 'd0d5dd' },
      right:  { style: BorderStyle.SINGLE, size: 4, color: 'd0d5dd' },
      insideHorizontal: { style: BorderStyle.SINGLE, size: 2, color: 'e4e7ec' },
      insideVertical:   { style: BorderStyle.SINGLE, size: 2, color: 'e4e7ec' },
    },
  });
}

function buildHeader(rfqId: string): Header {
  return new Header({
    children: [
      new Paragraph({
        alignment: AlignmentType.RIGHT,
        children: [
          new TextRun({ text: `Datacrate — RFQ ${rfqId}`, color: GRAY, size: 16 }),
        ],
      }),
    ],
  });
}

function buildFooter(): Footer {
  return new Footer({
    children: [
      new Paragraph({
        alignment: AlignmentType.CENTER,
        children: [
          new TextRun({
            text: 'Confidential — Request For Quote.  Do not redistribute.',
            color: GRAY,
            size: 16,
          }),
        ],
      }),
    ],
  });
}

/**
 * Build a full RFQ Document. Returns the `BuiltRfq` bundle — the
 * caller drives `Packer.toBlob(rfq.document)` and `saveAs(blob, rfq.filename)`.
 *
 * Why split the bundle: tests can pin the plain-text round-trip
 * without spinning up DOM globals; the UI layer only needs the docx
 * Document.
 */
export function buildRfqDocument(opts: RfqOptions): BuiltRfq {
  const item = opts.bomItem;
  const proj = opts.project;
  const issueDate = opts.issueDate ?? new Date().toISOString();
  const seq = opts.seq ?? 1;
  const rfqId = buildRfqId(proj.siteId, item.id, seq);
  const filename = buildRfqFilename(rfqId, proj.siteName, item.name);
  const plainText = buildRfqPlainText({ ...opts, issueDate, seq });
  const compliance = (item.complianceRequirements ?? []).join(', ') || '—';

  const children: (Paragraph | Table)[] = [];

  // ── Title block ──────────────────────────────────────────────────
  children.push(h1('Request For Quote (RFQ)'));
  children.push(
    kvTable([
      { label: 'RFQ ID',            value: rfqId },
      { label: 'Issue Date',        value: fmtDate(issueDate) },
      { label: 'Response Deadline', value: fmtDate(opts.responseDeadline) },
      { label: 'Project',           value: proj.siteName },
      ...(proj.issuingCompany ? [{ label: 'Issued By', value: proj.issuingCompany }] : []),
      ...(opts.supplierName   ? [{ label: 'To',        value: opts.supplierName }]   : []),
      ...(proj.contactName || proj.contactEmail
        ? [
            {
              label: 'Reply To',
              value: `${proj.contactName ?? ''}${
                proj.contactEmail ? ` <${proj.contactEmail}>` : ''
              }`.trim(),
            },
          ]
        : []),
    ]),
  );

  // ── Project context ──────────────────────────────────────────────
  children.push(h2('Project Context'));
  children.push(
    kvTable([
      { label: 'Site Address',       value: proj.siteAddress },
      { label: 'Commercial Op Date', value: fmtDate(proj.cod) },
      { label: 'Technology',         value: proj.technologyLabel ?? '—' },
      {
        label: 'Required On-Site',
        value: fmtDate(item.requiredOnSiteDate) +
          (item.requiredOnSiteDate ? '' : '  (derived from COD − install duration)'),
      },
    ]),
  );

  // ── Item description ─────────────────────────────────────────────
  children.push(h2('Item Description'));
  children.push(
    kvTable([
      { label: 'Category',            value: SCM_CATEGORY_LABELS[item.category] },
      { label: 'Item',                value: item.name },
      { label: 'Specification',       value: item.spec || '—' },
      { label: 'Quantity',            value: `${item.quantity} ${item.unit}` },
      { label: BOM_CRITICALITY_FIELD_LABEL, value: labelBomCriticality(item.criticality) },
      { label: BOM_SUBSTITUTABILITY_FIELD_LABEL, value: labelBomSubstitutability(item.substitutability) },
      { label: 'Compliance Required', value: compliance },
      ...(item.notes ? [{ label: 'Notes', value: item.notes }] : []),
    ]),
  );

  // ── Vendor response — fillable section. Labels match SCM8 patterns. ──
  children.push(h2('Vendor Response — please complete'));
  children.push(
    kvTable([
      { label: 'Unit Price',     value: '$______________________________' },
      { label: 'Lead Time',      value: '__________ months ARO' },
      { label: 'Quote Validity', value: '__________ days from issue' },
      { label: 'Payment Terms',  value: '____________________________________________' },
      { label: 'Incoterms',      value: '__________   (FOB / DDP / CIF / EXW / CFR / DAP)' },
      { label: 'Currency',       value: '__________   (USD / EUR / GBP / etc.)' },
      { label: 'Compliance Certs Attached', value: '___ Yes   ___ No   (attach copies)' },
      { label: 'Notes / Exclusions',        value: '____________________________________________' },
    ]),
  );

  // ── Standard terms boilerplate ───────────────────────────────────
  children.push(h2('Standard Terms'));
  children.push(
    p(
      '1. Confidentiality. This RFQ and any information disclosed by the issuer is confidential and shall not be shared with third parties without prior written consent.',
    ),
  );
  children.push(
    p(
      '2. Response Format. Vendor may respond by completing this template and returning it as a signed PDF, or by attaching the vendor’s standard quote document. Pricing, lead time, validity, payment terms and Incoterms must all be present in the response.',
    ),
  );
  children.push(
    p(
      '3. Non-Binding. This RFQ is a request for information; it does not constitute a purchase order or commitment to purchase.',
    ),
  );
  children.push(
    p(
      '4. Compliance. Vendor confirms ability to meet the certification requirements listed above, or shall clearly note exceptions.',
    ),
  );
  children.push(p(''));
  children.push(p('Authorised Signature: ___________________________   Date: __________'));

  const document = new Document({
    creator: 'Datacrate',
    title: `RFQ ${rfqId} — ${item.name}`,
    subject: 'Request For Quote',
    description: `RFQ for ${item.name} (${proj.siteName})`,
    keywords: ['Datacrate', 'RFQ', proj.siteName, item.name].filter(Boolean).join(', '),
    styles: {
      default: {
        document: { run: { font: 'Calibri', size: 20 } },
        heading1: { run: { font: 'Calibri', size: 32, bold: true, color: BRAND } },
        heading2: { run: { font: 'Calibri', size: 24, bold: true, color: DARK } },
      },
    },
    sections: [
      {
        headers: { default: buildHeader(rfqId) },
        footers: { default: buildFooter() },
        children,
      },
    ],
  });

  return { rfqId, filename, plainText, document };
}

/**
 * Convenience — generate the DOCX as a Blob ready for `saveAs`. UI
 * layer calls this; tests stick to `buildRfqDocument` so they don't
 * have to mock browser globals.
 */
export async function packRfqBlob(built: BuiltRfq): Promise<Blob> {
  return Packer.toBlob(built.document);
}

// ─────────────────────────────────────────────────────────────────────────────
// Batch builder — one RFQ document per BoM item.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Batch entry point. Returns one `BuiltRfq` per BoM item, sharing the
 * same project context + deadline. Used by the Suppliers tab's
 * "Generate RFQ Pack" CTA so a single click produces all the
 * docs for a supplier's shortlisted items.
 */
export function buildRfqBatch(args: {
  items: BomItem[];
  project: RfqProjectContext;
  responseDeadline: string;
  issueDate?: string;
  supplierName?: string;
  /** Sequence base — first item gets `seq`, second `seq+1`, etc. Default 1. */
  seqBase?: number;
}): BuiltRfq[] {
  const seqBase = args.seqBase ?? 1;
  return args.items.map((bomItem, i) =>
    buildRfqDocument({
      bomItem,
      project: args.project,
      responseDeadline: args.responseDeadline,
      issueDate: args.issueDate,
      supplierName: args.supplierName,
      seq: seqBase + i,
    }),
  );
}
